import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import HelmetTags from "../../../../MainComponents/HelmetTags.jsx";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faLock,
  faPen,
  faPlus,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useAuthState } from "../../../../MainComponents/GlobalContext.jsx";
import useAxios from "../../../../MainComponents/Hooks/useAxios.jsx";
import { Popconfirm } from "antd";

const PAGE_SIZE = 12;

const COURSE_TYPES = [
  { value: "", label: "All" },
  { value: "calculus", label: "Calculus" },
  { value: "algebra", label: "Algebra" },
  { value: "statistics", label: "Statistics" },
  { value: "dynamics", label: "Dynamics" },
  { value: "statics", label: "Statics" },
  { value: "applied", label: "Applied" },
  { value: "pure", label: "Pure" },
];

const STAGES = [1, 2, 3];
const BATCHES = ["all", "2024-2025", "2025-2026", "2026-2027"];
const ACADEMIC_TYPES = ["scientific", "literary"];

const FilterButton = React.memo(({ label, isActive, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`z-10 flex h-full cursor-pointer items-center justify-center font-semibold transition-all duration-200 ${className} ${
      isActive ? "bg-green" : ""
    }`}
  >
    {label}
  </button>
));

FilterButton.displayName = "FilterButton";

function getStageLabel(stage) {
  if (+stage === 1) return "1st sec";
  if (+stage === 2) return "2nd sec";
  if (+stage === 3) return "3rd sec";
  return "";
}

const CourseCard = React.memo(function CourseCard({
  course,
  admin,
  onDelete,
  hasViewPermission,
  hasDeletePermission,
  hasEditPermission,
}) {
  const navigate = useNavigate();

  const handleImageClick = useCallback(() => {
    navigate(
      admin ? `/admin/courses/${course.key}` : `/home/courses/${course.key}`,
    );
  }, [admin, course.key, navigate]);

  return (
    <article className="admin-course-card">
      <div className="admin-course-card__media" onClick={handleImageClick}>
        <img
          src={course.img}
          alt={course.title}
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="admin-course-card__body">
        <div className="admin-course-card__meta">
          <span className="admin-course-card__badge">{course.type}</span>
          {Number(course.stage) === 3 && (
            <span className="admin-course-card__badge">
              {course.system_type === "all"
                ? "للكل"
                : course.system_type === "hand_by_hand"
                  ? "ايد بايد"
                  : "النظام العادي"}
            </span>
          )}
          <p className="admin-course-card__stats">
            {course.n_sessions} Sessions ·{" "}
            {course.price === 0 ? "Free" : `${course.price} EGP`} ·{" "}
            {getStageLabel(course.stage)}
          </p>
        </div>

        <h2 className="admin-course-card__title">
          <Link
            to={
              admin
                ? `/admin/courses/${course.key}`
                : `/home/courses/${course.key}`
            }
          >
            {course.title}
          </Link>
        </h2>

        <p className="admin-course-card__desc">{course.description}</p>

        <div className="admin-course-card__actions">
          {admin ? (
            <>
              {hasViewPermission && (
                <Link
                  to={`/admin/courses/view/${course.key}`}
                  className="admin-course-card__btn admin-course-card__btn--view"
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>View</span>
                </Link>
              )}
              {hasEditPermission && (
                <Link
                  to={`/admin/courses/edit-course/${course.key}`}
                  className="admin-course-card__btn admin-course-card__btn--edit"
                >
                  <FontAwesomeIcon icon={faPen} />
                  <span>Edit</span>
                </Link>
              )}
              {hasDeletePermission && (
                <Popconfirm
                  okText="Confirm"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => onDelete(course.key)}
                  title="Are You Sure You Want to Delete The Course?"
                >
                  <button
                    type="button"
                    className="admin-course-card__btn admin-course-card__btn--delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    <span>Delete</span>
                  </button>
                </Popconfirm>
              )}
            </>
          ) : (
            <Link
              to="/"
              className="admin-course-card__btn admin-course-card__btn--buy"
            >
              <FontAwesomeIcon icon={faLock} />
              Buy Course
            </Link>
          )}
        </div>
      </div>
    </article>
  );
});

function AdminCourses({ admin }) {
  const AuthState = useAuthState();
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [reFetch, setReFetch] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    courseType: "",
    stage: 1,
    batch: "all",
    academicType: "scientific",
    systemType: "normal",
    search: "",
  });

  const [allCoursesData, allCoursesErrors, allCoursesLoading] = useAxios(
    process.env.REACT_APP_ADMIN_COURSES_TAB_API,
    "GET",
    "GET",
    reFetch,
  );

  const [submitDelete, setSubmitDelete] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  const [deleteCourseSuccess] = useAxios(
    process.env.REACT_APP_ADMIN_DELETE_COURSE_API,
    "POST",
    submitDelete.flag,
    submitDelete.dependency,
    submitDelete.data,
    true,
  );

  const handleDeleteCourse = useCallback((id) => {
    setSubmitDelete((prev) => ({
      flag: "deleteCourse",
      dependency: !prev.dependency,
      data: { id },
    }));
  }, []);

  useEffect(() => {
    if (deleteCourseSuccess) {
      setReFetch((prev) => !prev);
    }
  }, [deleteCourseSuccess]);

  const permissions = useMemo(() => {
    const isSuperAdmin =
      AuthState.userData?.admin?.roles[0]?.name === "superadmin";
    const userPermissions = AuthState.userData?.admin?.permissions || [];

    return {
      canAdd:
        isSuperAdmin ||
        userPermissions.some((p) => p.name.startsWith("lecture_add")),
      canView:
        isSuperAdmin ||
        userPermissions.some((p) => p.name.startsWith("lecture_view")),
      canDelete:
        isSuperAdmin ||
        userPermissions.some((p) => p.name.startsWith("lecture_delete")),
      canEdit:
        isSuperAdmin ||
        userPermissions.some((p) => p.name.startsWith("lecture_edit")),
    };
  }, [AuthState.userData?.admin]);

  const filteredCourses = useMemo(() => {
    let filtered = allCoursesData?.data || [];

    if (filters.courseType) {
      filtered = filtered.filter(
        (course) => course.branch === filters.courseType,
      );
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((course) =>
        course.title?.toLowerCase().includes(searchLower),
      );
    }

    filtered = filtered.filter((course) => course.stage === filters.stage);

    if (filters.batch !== "all") {
      filtered = filtered.filter((course) => course.batch === filters.batch);
    }

    if (filters.stage === 3) {
      filtered = filtered.filter(
        (course) => course.section === filters.academicType,
      );
    }

    if (filters.stage === 3) {
      filtered = filtered.filter(
        (course) => (course.system_type || "normal") === filters.systemType,
      );
    }

    return filtered;
  }, [allCoursesData?.data, filters]);

  // Reset visible window when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters]);

  const visibleCourses = useMemo(
    () => filteredCourses.slice(0, visibleCount),
    [filteredCourses, visibleCount],
  );

  const hasMore = visibleCount < filteredCourses.length;

  // Infinite scroll inside the page scroll container
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, filteredCourses.length),
          );
        }
      },
      { root, rootMargin: "200px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, filteredCourses.length, visibleCourses.length]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value }));
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  return (
    <section
      ref={scrollRef}
      className="admin-courses-page dark:bg-dark dark:text-light h-full w-full overflow-y-auto px-10 py-10 sm:px-4"
    >
      <HelmetTags title="Courses | Mr.Mohamed Salama" />

      <div className="mb-10 flex flex-col items-start gap-8 amd-hero:items-center">
        <div className="flex w-full flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-bold">Courses</h2>
          {permissions.canAdd && (
            <Link
              to="/admin/courses/add-course"
              className="rounded-lg border-2 border-secondary bg-secondary px-3 py-1.5 font-semibold text-bg transition-all duration-300 hover:brightness-110 active:scale-90"
            >
              Add Course <FontAwesomeIcon className="ml-2" icon={faPlus} />
            </Link>
          )}
        </div>

        <div className="flex w-full flex-wrap justify-center gap-3 bg-slate-100 py-3 text-slate-800 dark:bg-[#121a35] dark:text-slate-100 sm:gap-4">
          {COURSE_TYPES.map((type) => (
            <FilterButton
              key={type.value}
              label={type.label}
              isActive={filters.courseType === type.value}
              onClick={() => updateFilter("courseType", type.value)}
              className="rounded-md px-3 py-1"
            />
          ))}
        </div>

        <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
          <div className="filters relative flex h-12 w-[800px] items-center justify-between">
            {STAGES.map((stageNum) => (
              <FilterButton
                key={stageNum}
                label={getStageLabel(stageNum)}
                isActive={filters.stage === stageNum}
                onClick={() => updateFilter("stage", stageNum)}
                className="w-1/3"
              />
            ))}
          </div>
        </div>

        <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
          <div className="filters relative flex h-12 w-[800px] items-center justify-between">
            {BATCHES.map((batchOption) => (
              <FilterButton
                key={batchOption}
                label={batchOption === "all" ? "All" : batchOption}
                isActive={filters.batch === batchOption}
                onClick={() => updateFilter("batch", batchOption)}
                className="w-1/4"
              />
            ))}
          </div>
        </div>

        {filters.stage === 3 && (
          <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
            <div className="filters relative flex h-12 w-[800px] items-center justify-between">
              {ACADEMIC_TYPES.map((type) => (
                <FilterButton
                  key={type}
                  label={type.charAt(0).toUpperCase() + type.slice(1)}
                  isActive={filters.academicType === type}
                  onClick={() => updateFilter("academicType", type)}
                  className="w-1/2"
                />
              ))}
            </div>
          </div>
        )}

        {filters.stage === 3 && (
          <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
            <div className="filters relative flex h-12 w-[900px] items-center justify-between">
              <FilterButton
                label="للكل"
                isActive={filters.systemType === "all"}
                onClick={() => updateFilter("systemType", "all")}
                className="w-1/3"
              />
              <FilterButton
                label="ايد بايد"
                isActive={filters.systemType === "hand_by_hand"}
                onClick={() => updateFilter("systemType", "hand_by_hand")}
                className="w-1/3"
              />
              <FilterButton
                label="النظام العادي"
                isActive={filters.systemType === "normal"}
                onClick={() => updateFilter("systemType", "normal")}
                className="w-1/3"
              />
            </div>
          </div>
        )}

        <div className="mx-auto w-full max-w-xl">
          <input
            className="signin-inputs"
            placeholder="Search courses..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="courses w-full">
        {allCoursesLoading && !allCoursesData?.data ? (
          <div className="admin-courses-sentinel">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Loading courses...
          </div>
        ) : allCoursesErrors ? (
          <p className="admin-courses-empty">
            Something Wrong has happened, Please Try Again.
          </p>
        ) : filteredCourses.length === 0 ? (
          <p className="admin-courses-empty">There are No Courses Yet.</p>
        ) : (
          <>
            <div className="admin-courses-grid">
              {visibleCourses.map((course) => (
                <CourseCard
                  key={course.key}
                  course={course}
                  admin={admin}
                  onDelete={handleDeleteCourse}
                  hasViewPermission={permissions.canView}
                  hasDeletePermission={permissions.canDelete}
                  hasEditPermission={permissions.canEdit}
                />
              ))}
            </div>

            <div ref={sentinelRef} className="admin-courses-sentinel">
              {hasMore ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Loading more...
                </>
              ) : (
                `Showing all ${filteredCourses.length} courses`
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default AdminCourses;
